import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  User, Briefcase, MapPin, GraduationCap, Code, Languages, Award,
  CheckCircle, Loader2, Camera, QrCode, Share2, Clock, Calendar,
  Building2, TrendingUp, Shield, Globe, Target, ExternalLink, Settings,
  Smartphone, Lock, Unlock, Eye, EyeOff, ChevronRight, Sparkles, Star, Edit,
  Save, X, LogOut, Bell, FileText, Home, Search, Heart, Bookmark,
  AlertCircle, Upload, Link as LinkIcon, AtSign, RefreshCw, Trash2, Plus, DollarSign,
  Phone, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { MasterSelect } from '../components/ui/master-select';
import VerificationTab from '../components/portal/VerificationTab';
import { TagInput } from '../components/ui/tag-input';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate initials from name
const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Format name properly (Title Case)
const formatName = (name) => {
  if (!name) return '';
  return name.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

// Privacy Toggle Component
function PrivacyToggle({ isPublic, onToggle, label }) {
  return (
    <button
      onClick={onToggle}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
        isPublic 
          ? 'bg-green-100 text-green-700 hover:bg-green-200' 
          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
      }`}
    >
      {isPublic ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
      {isPublic ? 'Public' : 'Hidden'}
    </button>
  );
}


// Privacy Controls Card - Per-Field Toggles
function PrivacyControlsCard({ candidate, loadProfile }) {
  const [saving, setSaving] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({
    show_photo_public: candidate?.show_photo_public !== false,
    show_summary_public: candidate?.show_summary_public !== false,
    show_skills_public: candidate?.show_skills_public !== false,
    show_experience_public: candidate?.show_experience_public !== false,
    show_education_public: candidate?.show_education_public !== false,
    show_certifications_public: candidate?.show_certifications_public !== false,
    show_languages_public: candidate?.show_languages_public !== false,
    show_compensation_public: candidate?.show_compensation_public || false,
    show_current_salary_public: candidate?.show_current_salary_public || false,
    show_expected_salary_public: candidate?.show_expected_salary_public || false,
    show_notice_period_public: candidate?.show_notice_period_public || false,
    show_location_public: candidate?.show_location_public !== false,
    show_designation_public: candidate?.show_designation_public !== false,
    show_company_public: candidate?.show_company_public !== false,
    show_experience_years_public: candidate?.show_experience_years_public !== false,
    show_linkedin_public: candidate?.show_linkedin_public !== false,
    show_gender_public: candidate?.show_gender_public !== false,
    show_work_preference_public: candidate?.show_work_preference_public !== false
  });

  const toggleSetting = (key) => {
    setPrivacySettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const savePrivacySettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('candidate_token');
      const res = await fetch(`${API_URL}/api/candidate/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(privacySettings)
      });

      if (!res.ok) throw new Error('Failed to save');
      toast.success('Privacy settings saved!');
      loadProfile();
    } catch (error) {
      toast.error('Failed to save privacy settings');
    } finally {
      setSaving(false);
    }
  };

  const privacyFields = [
    { key: 'show_photo_public', label: 'Profile Photo', description: 'Your photo on public profile' },
    { key: 'show_summary_public', label: 'Professional Summary', description: 'About me section' },
    { key: 'show_designation_public', label: 'Current Title', description: 'Your job designation' },
    { key: 'show_company_public', label: 'Current Company', description: 'Where you work' },
    { key: 'show_location_public', label: 'Location', description: 'City/country info' },
    { key: 'show_experience_years_public', label: 'Years of Experience', description: 'Total experience' },
    { key: 'show_skills_public', label: 'Skills', description: 'Technical and soft skills' },
    { key: 'show_experience_public', label: 'Work History', description: 'Past job experiences' },
    { key: 'show_education_public', label: 'Education', description: 'Academic qualifications' },
    { key: 'show_certifications_public', label: 'Certifications', description: 'Professional certifications' },
    { key: 'show_languages_public', label: 'Languages', description: 'Languages you speak' },
    { key: 'show_linkedin_public', label: 'LinkedIn Profile', description: 'Link to LinkedIn' },
    { key: 'show_gender_public', label: 'Gender', description: 'Your gender' },
    { key: 'show_work_preference_public', label: 'Work Preference', description: 'Remote/On-site/Hybrid' }
  ];

  const compensationFields = [
    { key: 'show_current_salary_public', label: 'Current Salary', description: 'Your current CTC' },
    { key: 'show_expected_salary_public', label: 'Expected Salary', description: 'Your expected CTC' },
    { key: 'show_notice_period_public', label: 'Notice Period', description: 'Days to join' }
  ];

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-blue-600" />
          Privacy Controls
        </CardTitle>
        <CardDescription>Control what's visible on your public profile</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* General Fields */}
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-3">Profile Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {privacyFields.map(field => (
              <div 
                key={field.key}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
              >
                <div>
                  <p className="text-sm font-medium text-slate-800">{field.label}</p>
                  <p className="text-xs text-slate-500">{field.description}</p>
                </div>
                <Switch
                  checked={privacySettings[field.key]}
                  onCheckedChange={() => toggleSetting(field.key)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Compensation Fields - Sensitive */}
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-amber-600" />
            Compensation (Sensitive)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {compensationFields.map(field => (
              <div 
                key={field.key}
                className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100"
              >
                <div>
                  <p className="text-sm font-medium text-amber-800">{field.label}</p>
                  <p className="text-xs text-amber-600">{field.description}</p>
                </div>
                <Switch
                  checked={privacySettings[field.key]}
                  onCheckedChange={() => toggleSetting(field.key)}
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-amber-600 mt-2">
            ⚠️ Compensation details are hidden by default. Enable only if you want employers to see this.
          </p>
        </div>

        <Button onClick={savePrivacySettings} disabled={saving} className="w-full">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Privacy Settings
        </Button>
      </CardContent>
    </Card>
  );
}


// Section Header with Privacy Toggle
function SectionHeader({ icon: Icon, title, isPublic, onTogglePrivacy, showToggle = true }) {
  return (
    <div className="flex items-center justify-between">
      <CardTitle className="text-lg flex items-center gap-2">
        <Icon className="w-5 h-5 text-blue-600" />
        {title}
      </CardTitle>
      {showToggle && (
        <PrivacyToggle isPublic={isPublic} onToggle={onTogglePrivacy} />
      )}
    </div>
  );
}

// Job Matches Tab with multi-select, bulk apply/save, full details
function JobMatchesTab({ matches, candidate }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [savingId, setSavingId] = useState(null);
  const [savedJobs, setSavedJobs] = useState(new Set());
  const [appliedJobs, setAppliedJobs] = useState(new Set());
  const [expandedId, setExpandedId] = useState(null);
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [viewMode, setViewMode] = useState('all'); // 'all', 'saved', 'applied'
  const [savedJobsList, setSavedJobsList] = useState([]);
  const [appliedData, setAppliedData] = useState(null);

  // Initialize applied/saved state from matches
  useEffect(() => {
    const applied = new Set(matches.filter(m => m.applied).map(m => m.id));
    const saved = new Set(matches.filter(m => m.saved).map(m => m.id));
    setAppliedJobs(applied);
    setSavedJobs(saved);
  }, [matches]);

  const token = localStorage.getItem('candidate_token');

  const toggleSelect = (id) => {
    setSelectedJobs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedJobs.size === matches.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(matches.map(m => m.id)));
    }
  };

  const handleSaveJob = async (jobId) => {
    setSavingId(jobId);
    try {
      await fetch(`${API_URL}/api/candidate/job-results/${jobId}/save`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      setSavedJobs(prev => new Set([...prev, jobId]));
      toast.success('Job saved!');
    } catch (e) { toast.error('Failed to save'); }
    setSavingId(null);
  };

  const handleBulkSave = async () => {
    if (selectedJobs.size === 0) { toast.error('Select jobs first'); return; }
    setBulkSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/candidate/jobs/bulk-save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ job_ids: Array.from(selectedJobs) })
      });
      const data = await res.json();
      if (res.ok) {
        setSavedJobs(prev => new Set([...prev, ...selectedJobs]));
        toast.success(`${data.saved_count} jobs saved!`);
        setSelectedJobs(new Set());
      } else toast.error(data.detail || 'Failed to save');
    } catch (e) { toast.error('Failed to save jobs'); }
    setBulkSaving(false);
  };

  const handleBulkApply = async () => {
    if (selectedJobs.size === 0) { toast.error('Select jobs first'); return; }
    setBulkApplying(true);
    try {
      const res = await fetch(`${API_URL}/api/candidate/jobs/bulk-apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ job_ids: Array.from(selectedJobs) })
      });
      const data = await res.json();
      if (res.ok) {
        setAppliedJobs(prev => new Set([...prev, ...selectedJobs]));
        const companies = (data.applied_companies || []).map(c => c.company).join(', ');
        toast.success(`Applied to ${data.applied_count} jobs!${companies ? ` (${companies})` : ''}${data.skipped_already_applied > 0 ? ` ${data.skipped_already_applied} already applied` : ''}`);
        setSelectedJobs(new Set());
        // Refresh applied data
        loadAppliedJobs();
      } else toast.error(data.detail || 'Failed to apply');
    } catch (e) { toast.error('Failed to apply'); }
    setBulkApplying(false);
  };

  const handleApplySingle = async (match) => {
    if (match.apply_link) {
      window.open(match.apply_link, '_blank');
    }
    // Also mark in our system
    try {
      await fetch(`${API_URL}/api/candidate/jobs/bulk-apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ job_ids: [match.id] })
      });
      setAppliedJobs(prev => new Set([...prev, match.id]));
    } catch (e) { /* non-critical */ }
  };

  const loadSavedJobs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/candidate/jobs/saved`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSavedJobsList(data.jobs || []);
      }
    } catch (e) { console.error(e); }
  };

  const loadAppliedJobs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/candidate/jobs/applied`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAppliedData(data);
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (viewMode === 'saved') loadSavedJobs();
    if (viewMode === 'applied') loadAppliedJobs();
  }, [viewMode]);

  const displayJobs = viewMode === 'saved' ? savedJobsList : matches;
  const selectableJobs = displayJobs.filter(m => !appliedJobs.has(m.id) && !m.applied);

  return (
    <div className="space-y-4" data-testid="job-matches-tab">
      {/* Header with tabs and bulk actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Your Job Matches</h2>
          <div className="flex items-center gap-2 mt-1">
            <button
              className={`text-sm px-3 py-1 rounded-full transition-colors ${viewMode === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              onClick={() => setViewMode('all')}
              data-testid="view-all-tab"
            >All ({matches.length})</button>
            <button
              className={`text-sm px-3 py-1 rounded-full transition-colors ${viewMode === 'saved' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              onClick={() => setViewMode('saved')}
              data-testid="view-saved-tab"
            >Saved</button>
            <button
              className={`text-sm px-3 py-1 rounded-full transition-colors ${viewMode === 'applied' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              onClick={() => setViewMode('applied')}
              data-testid="view-applied-tab"
            >Applied</button>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {displayJobs.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="bulk-action-bar">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedJobs.size > 0 && selectedJobs.size === selectableJobs.length}
              onChange={selectAll}
              className="w-4 h-4 rounded border-slate-300"
              data-testid="select-all-checkbox"
            />
            <span className="text-sm text-slate-600">
              {selectedJobs.size > 0 ? `${selectedJobs.size} selected` : 'Select all'}
            </span>
          </label>
          <div className="flex-1" />
          {selectedJobs.size > 0 && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="text-xs"
                onClick={handleBulkSave}
                disabled={bulkSaving}
                data-testid="bulk-save-btn"
              >
                {bulkSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Bookmark className="w-3 h-3 mr-1" />}
                Save Selected
              </Button>
              <Button
                size="sm"
                className="text-xs"
                onClick={handleBulkApply}
                disabled={bulkApplying}
                data-testid="bulk-apply-btn"
              >
                {bulkApplying ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Briefcase className="w-3 h-3 mr-1" />}
                Apply to Selected ({selectedJobs.size})
              </Button>
            </>
          )}
        </div>
      )}

      <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg flex items-center gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        Select multiple jobs and apply in one shot, or save them for later!
      </p>

      {/* Applied View */}
      {viewMode === 'applied' && (
        <div className="space-y-4" data-testid="applied-companies-view">
          {appliedData && appliedData.companies?.length > 0 ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary" className="text-sm">{appliedData.total_applications} applications</Badge>
                <Badge variant="outline" className="text-sm">{appliedData.companies.length} companies</Badge>
              </div>
              {appliedData.companies.map((company, idx) => (
                <Card key={idx} className="border border-slate-200 shadow-sm" data-testid={`applied-company-${idx}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-sm text-slate-800">{company.company_name}</h3>
                          <p className="text-xs text-slate-400">{company.count} position{company.count > 1 ? 's' : ''} applied</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1.5 mt-3">
                      {company.jobs.map((job, jIdx) => (
                        <div key={jIdx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <div>
                            <p className="text-xs font-medium text-slate-700">{job.job_title}</p>
                            <p className="text-[11px] text-slate-400">{job.applied_at ? new Date(job.applied_at).toLocaleDateString() : ''}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {job.match_score && (
                              <Badge className={`text-[10px] ${
                                job.match_score >= 80 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                              }`}>{job.match_score}%</Badge>
                            )}
                            {job.hr_shared ? (
                              <Badge className="text-[10px] bg-blue-100 text-blue-700">Shared with HR</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px]">{job.status || 'Submitted'}</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          ) : (
            <Card className="border-0 shadow-md">
              <CardContent className="p-12 text-center">
                <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-600">No applications yet</h3>
                <p className="text-sm text-slate-500 mt-2">Apply to jobs from the All tab to see them here</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Job Cards (All & Saved views) */}
      {viewMode !== 'applied' && displayJobs.length > 0 ? (
        displayJobs.map((match, idx) => {
          const isSelected = selectedJobs.has(match.id);
          const isApplied = appliedJobs.has(match.id) || match.applied;
          const isSaved = savedJobs.has(match.id) || match.saved;

          return (
            <Card
              key={match.id || idx}
              className={`border shadow-sm hover:shadow-md transition-all cursor-pointer ${isSelected ? 'border-blue-400 bg-blue-50/30 ring-1 ring-blue-200' : 'border-slate-200'} ${isApplied ? 'opacity-75' : ''}`}
              data-testid={`job-match-${idx}`}
              onClick={() => !isApplied && toggleSelect(match.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <div className="flex-shrink-0 pt-1" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => !isApplied && toggleSelect(match.id)}
                      disabled={isApplied}
                      className="w-4 h-4 rounded border-slate-300"
                      data-testid={`job-checkbox-${idx}`}
                    />
                  </div>

                  {/* Job Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          {match.company_logo ? (
                            <img src={match.company_logo} alt="" className="w-7 h-7 rounded" />
                          ) : (
                            <Building2 className="w-5 h-5 text-slate-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm text-slate-800">{match.job_title}</h3>
                          <p className="text-xs text-slate-500">{match.company_name || match.company}</p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            {match.location && (
                              <span className="text-[11px] text-slate-400 flex items-center gap-0.5">
                                <MapPin className="w-3 h-3" />{match.location}
                              </span>
                            )}
                            {match.salary && (
                              <span className="text-[11px] text-green-600 font-medium flex items-center gap-0.5">
                                <DollarSign className="w-3 h-3" />{match.salary}
                              </span>
                            )}
                            {match.job_type && <Badge variant="outline" className="text-[10px] py-0">{match.job_type}</Badge>}
                            {match.work_from_home && <Badge variant="outline" className="text-[10px] py-0 text-blue-600 border-blue-200">Remote</Badge>}
                            {match.posted_at && <span className="text-[11px] text-slate-400">{match.posted_at}</span>}
                            {match.cross_matched_from && <Badge variant="outline" className="text-[10px] py-0 text-purple-600 border-purple-200">Trending</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={`text-[10px] px-2 ${
                          (match.match_score || 0) >= 80 ? 'bg-green-100 text-green-700' :
                          (match.match_score || 0) >= 60 ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {match.match_score || 0}%
                        </Badge>
                        {isApplied && <Badge className="text-[10px] bg-blue-100 text-blue-700">Applied</Badge>}
                      </div>
                    </div>

                    {/* Description */}
                    {match.description && (
                      <p className={`text-xs text-slate-600 mt-2 ${expandedId === match.id ? '' : 'line-clamp-2'}`}>
                        {match.description}
                      </p>
                    )}
                    {match.description && match.description.length > 200 && (
                      <button className="text-[11px] text-blue-600 hover:underline mt-0.5"
                        onClick={e => { e.stopPropagation(); setExpandedId(expandedId === match.id ? null : match.id); }}>
                        {expandedId === match.id ? 'Show less' : 'Read more'}
                      </button>
                    )}

                    {/* Requirements/Benefits */}
                    {match.requirements?.length > 0 && (
                      <div className="mt-1.5">
                        <span className="text-[11px] font-medium text-slate-500">Requirements: </span>
                        <span className="text-[11px] text-slate-600">{match.requirements.slice(0, 3).join(' | ')}</span>
                      </div>
                    )}
                    {match.benefits?.length > 0 && (
                      <div className="mt-0.5">
                        <span className="text-[11px] font-medium text-emerald-600">Benefits: </span>
                        <span className="text-[11px] text-slate-600">{match.benefits.slice(0, 3).join(' | ')}</span>
                      </div>
                    )}

                    {/* Individual Actions */}
                    <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-slate-100" onClick={e => e.stopPropagation()}>
                      {!isApplied && (
                        <Button size="sm" className="text-xs h-7" onClick={() => handleApplySingle(match)} data-testid={`apply-btn-${idx}`}>
                          <ExternalLink className="w-3 h-3 mr-1" />Apply
                        </Button>
                      )}
                      {!isSaved && !isApplied && (
                        <Button size="sm" variant="outline" className="text-xs h-7"
                          onClick={() => handleSaveJob(match.id)}
                          disabled={savingId === match.id}
                          data-testid={`save-btn-${idx}`}>
                          {savingId === match.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Bookmark className="w-3 h-3 mr-1" />}
                          Save
                        </Button>
                      )}
                      {isSaved && <span className="text-xs text-slate-400 flex items-center gap-1"><Heart className="w-3 h-3 fill-current text-red-400" />Saved</span>}
                      {isApplied && <span className="text-xs text-blue-500 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Applied</span>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })
      ) : viewMode !== 'applied' ? (
        <Card className="border-0 shadow-md">
          <CardContent className="p-12 text-center">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-600">{viewMode === 'saved' ? 'No saved jobs yet' : 'No job matches yet'}</h3>
            <p className="text-sm text-slate-500 mt-2">
              {viewMode === 'saved' ? 'Save jobs from your matches to view them here' : 'Complete your profile and share on LinkedIn to unlock job matches'}
            </p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}



// Main Dashboard Component
function Dashboard({ candidate, matches, loadMatches }) {
  const navigate = useNavigate();
  const [unlockStatus, setUnlockStatus] = useState(null);
  const [unlockLoading, setUnlockLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [shareContent, setShareContent] = useState(null);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadUnlockStatus();
  }, []);

  const loadUnlockStatus = async () => {
    try {
      const token = localStorage.getItem('candidate_token');
      const res = await fetch(`${API_URL}/api/candidate/unlock-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setUnlockStatus(await res.json());
      }
    } catch (e) { console.error('Unlock status error:', e); }
    setUnlockLoading(false);
  };

  const loadShareContent = async () => {
    try {
      const token = localStorage.getItem('candidate_token');
      const res = await fetch(`${API_URL}/api/candidate/linkedin-share-content`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setShareContent(data);
        return data;
      }
    } catch (e) { console.error(e); }
    return null;
  };

  const handleUnlockAction = async (action) => {
    if (action === 'linkedin_share') {
      const content = await loadShareContent();
      if (content) {
        // Open LinkedIn compose with pre-filled post text
        window.open(content.linkedin_share_url, '_blank');
        // After user comes back, register the unlock
        setPendingAction(action);
        setShowUnlockModal(true);
      }
      return;
    }

    // For other actions, check if the candidate has completed the action
    const actionRouteMap = {
      'add_picture': '/my/profile',
      'add_profile_info': '/my/profile',
      'add_certifications': '/my/profile',
      'add_languages': '/my/profile',
      'add_skills': '/my/profile',
      'add_education': '/my/profile',
      'add_compensation': '/my/salary-history',
      'add_username': '/my/settings',
      'hide_linkedin': '/my/settings',
    };

    if (actionRouteMap[action]) {
      navigate(actionRouteMap[action]);
    }
  };

  const confirmUnlock = async (action) => {
    setUnlocking(true);
    try {
      const token = localStorage.getItem('candidate_token');
      const res = await fetch(`${API_URL}/api/candidate/unlock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action, metadata: {} })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Unlocked! Searching for jobs...');
        setShowUnlockModal(false);
        await loadUnlockStatus();
        // Reload matches after a delay (job search runs in background)
        setTimeout(() => loadMatches(), 5000);
      } else {
        toast.error(data.detail || 'Could not unlock');
      }
    } catch (e) {
      toast.error('Error performing unlock');
    }
    setUnlocking(false);
  };

  // Auto-unlock when profile sections are completed
  const triggerAutoUnlock = async (actionType) => {
    try {
      const token = localStorage.getItem('candidate_token');
      const res = await fetch(`${API_URL}/api/candidate/auto-unlock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action_type: actionType })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || `${actionType} unlock activated!`);
        await loadUnlockStatus();
        // Reload matches after a delay (job search runs in background)
        setTimeout(() => loadMatches(), 5000);
        return true;
      }
      return false;
    } catch (e) {
      console.error('Auto-unlock error:', e);
      return false;
    }
  };
  
  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    const num = typeof amount === 'string' ? parseInt(amount.replace(/[^0-9]/g, '')) : amount;
    if (num >= 10000000) return `${(num / 10000000).toFixed(1)} Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(1)} LPA`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num?.toLocaleString('en-IN') || 'N/A';
  };

  const salaryProgression = candidate?.salary_progression || [];
  const maxCtc = salaryProgression.length > 0 ? Math.max(...salaryProgression.map(e => e.estimated_annual_ctc || 0)) : 0;
  const docsCount = (candidate?.documents?.length || 0) + (candidate?.original_file_name ? 1 : 0);
  
  return (
    <div className="space-y-5" data-testid="candidate-dashboard">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 rounded-xl overflow-hidden relative" data-testid="welcome-banner">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(59,130,246,0.15),transparent)]" />
        <div className="p-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Welcome back, {formatName(candidate?.name)?.split(' ')[0] || 'there'}!</h1>
              <p className="text-slate-300 mt-1 text-sm">{candidate?.current_designation ? `${candidate.current_designation} at ${candidate.current_company || ''}` : 'Complete your profile to get better matches'}</p>
            </div>
            <div className="text-right space-y-1">
              <p className="text-xs text-slate-400 uppercase tracking-wider">Profile Views</p>
              <p className="text-4xl font-black tracking-tight text-white">{candidate?.profile_views || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-job-matches">
          <CardContent className="p-4 text-center">
            <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Target className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-slate-800">{matches?.length || 0}</p>
            <p className="text-xs text-slate-500">Job Matches</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-applications">
          <CardContent className="p-4 text-center">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-slate-800">{candidate?.applications?.length || 0}</p>
            <p className="text-xs text-slate-500">Applications</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-documents">
          <CardContent className="p-4 text-center">
            <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center mx-auto mb-2">
              <FileText className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-slate-800">{docsCount}</p>
            <p className="text-xs text-slate-500">Documents</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-views">
          <CardContent className="p-4 text-center">
            <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center mx-auto mb-2">
              <Eye className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-slate-800">{candidate?.profile_views || 0}</p>
            <p className="text-xs text-slate-500">Profile Views</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-est-ctc">
          <CardContent className="p-4 text-center">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center mx-auto mb-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-2xl font-bold text-slate-800">{formatCurrency(candidate?.estimated_ctc)}</p>
            <p className="text-xs text-slate-500">Est. CTC</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        {/* Salary Progression Graph - Now Editable */}
        <Card className="lg:col-span-2 border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
                <TrendingUp className="w-4 h-4 text-green-600" />Career Salary History
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/my/salary-history')}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                <Edit className="w-3 h-3 mr-1" />Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {salaryProgression.length > 0 ? (
              <div className="space-y-2">
                {salaryProgression.map((entry, idx) => {
                  const pct = maxCtc > 0 ? ((entry.estimated_annual_ctc || 0) / maxCtc) * 100 : 0;
                  return (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-400 w-8 text-right font-mono">{entry.year}</span>
                      <div className="flex-1">
                        <div className="h-7 bg-slate-100 rounded-md overflow-hidden relative">
                          <div 
                            className="h-full rounded-md transition-all"
                            style={{ 
                              width: `${Math.max(pct, 12)}%`,
                              background: `linear-gradient(90deg, #34d399 0%, #059669 ${Math.min(pct, 100)}%)`
                            }}
                          />
                          <span className="absolute inset-0 flex items-center px-2 text-[10px] font-medium text-slate-700 truncate">
                            {entry.role} — {formatCurrency(entry.estimated_annual_ctc)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-slate-400">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>No salary history yet</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => navigate('/my/salary-history')}
                >
                  <Plus className="w-3 h-3 mr-1" />Add History
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Job Matches Timeline - With Unlock Overlay */}
        <Card className="lg:col-span-3 border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
                <Briefcase className="w-4 h-4 text-blue-600" />Your Job Matches
              </CardTitle>
              {matches?.length > 5 && (
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/my/jobs')}>View All</Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0 relative">
            {/* Unlock overlay - shown when candidate needs to unlock */}
            {unlockStatus && unlockStatus.total_unlocks === 0 && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-md rounded-lg p-6" data-testid="unlock-overlay">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                  <Lock className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 text-center">Unlock Your Job Matches</h3>
                <p className="text-sm text-slate-500 text-center mt-2 max-w-xs">
                  Share about Daruwallas on LinkedIn to unlock AI-powered job matches in your locality
                </p>
                <Button 
                  className="mt-4 bg-[#0077B5] hover:bg-[#006097] text-white"
                  onClick={() => handleUnlockAction('linkedin_share')}
                  disabled={unlocking}
                  data-testid="linkedin-share-unlock-btn"
                >
                  {unlocking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                  Share on LinkedIn
                </Button>
                <p className="text-xs text-slate-400 mt-2">This also runs AI job search for your profile</p>
              </div>
            )}

            {/* Blurred content behind overlay */}
            <div className={unlockStatus && unlockStatus.total_unlocks === 0 ? 'filter blur-sm select-none' : ''}>
              {matches?.length > 0 ? (
                <div className="space-y-2">
                  {matches.slice(0, 5).map((match, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                      data-testid={`match-item-${idx}`}
                    >
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                        <Building2 className="w-5 h-5 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm text-slate-800 truncate">{match.job_title}</h3>
                        <p className="text-xs text-slate-500">{match.company_name || match.company}</p>
                      </div>
                      <Badge className={`text-[10px] px-2 ${
                        (match.match_score || 0) >= 80 ? 'bg-green-100 text-green-700' :
                        (match.match_score || 0) >= 60 ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {match.match_score || 0}%
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Search className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No job matches yet. Complete your profile!</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Summary Preview */}
      {candidate?.ai_summary && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
              <Sparkles className="w-4 h-4 text-indigo-500" />AI Profile Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div 
              className="text-sm text-slate-600 leading-relaxed prose prose-sm max-w-none [&_b]:text-slate-800 [&_i]:text-indigo-600 line-clamp-4"
              dangerouslySetInnerHTML={{ __html: candidate.ai_summary }}
              data-testid="dashboard-ai-summary"
            />
          </CardContent>
        </Card>
      )}
      {/* Unlock Progress Card */}
      {unlockStatus && unlockStatus.total_unlocks > 0 && (
        <Card className="border-0 shadow-sm" data-testid="unlock-progress-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
                <Unlock className="w-4 h-4 text-green-600" />Unlock Progress
              </CardTitle>
              <Badge variant="secondary" className="text-xs">{unlockStatus.total_unlocks} unlocked</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="text-center p-2 bg-green-50 rounded-lg">
                <p className="text-lg font-bold text-green-700">{unlockStatus.jobs_unlocked}</p>
                <p className="text-[10px] text-slate-500">Jobs Unlocked</p>
              </div>
              <div className="text-center p-2 bg-blue-50 rounded-lg">
                <p className="text-lg font-bold text-blue-700">{unlockStatus.job_results_count}</p>
                <p className="text-[10px] text-slate-500">Total Matches</p>
              </div>
              <div className="text-center p-2 bg-purple-50 rounded-lg">
                <p className="text-lg font-bold text-purple-700">{unlockStatus.profile_views}</p>
                <p className="text-[10px] text-slate-500">Profile Views</p>
              </div>
              <div className="text-center p-2 bg-amber-50 rounded-lg">
                <p className="text-lg font-bold text-amber-700">{unlockStatus.applied_count}</p>
                <p className="text-[10px] text-slate-500">Applied</p>
              </div>
            </div>

            {/* Available Unlock Actions */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Complete to unlock more jobs</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {unlockStatus.available_actions?.filter(a => !a.completed || a.repeatable).slice(0, 6).map((action) => (
                  <button
                    key={action.action}
                    className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all text-left text-xs"
                    onClick={() => handleUnlockAction(action.action)}
                    data-testid={`unlock-action-${action.action}`}
                  >
                    {action.completed ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <Lock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}
                    <span className={action.completed ? 'text-slate-400 line-through' : 'text-slate-700'}>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* LinkedIn share countdown */}
            {!unlockStatus.linkedin_can_share && unlockStatus.linkedin_next_share_at && (
              <div className="mt-3 p-2 bg-blue-50 rounded-lg flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-blue-700">
                  Next LinkedIn share available: {new Date(unlockStatus.linkedin_next_share_at).toLocaleDateString()}
                </span>
              </div>
            )}

            {/* Cooldown indicator */}
            {unlockStatus.cooldown_remaining_seconds > 0 && (
              <div className="mt-2 p-2 bg-amber-50 rounded-lg flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />
                <span className="text-xs text-amber-700">
                  Cooldown active: {Math.floor(unlockStatus.cooldown_remaining_seconds / 3600)}h {Math.floor((unlockStatus.cooldown_remaining_seconds % 3600) / 60)}m remaining
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* LinkedIn Share Confirmation Modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowUnlockModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="unlock-confirm-modal">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-800">Confirm Your Share</h3>
              <p className="text-sm text-slate-500 mt-1">
                Did you share on LinkedIn? Click confirm to unlock AI job matches!
              </p>
            </div>
            
            {shareContent && (
              <div className="bg-slate-50 rounded-lg p-3 mb-4 max-h-32 overflow-y-auto">
                <p className="text-xs text-slate-600 whitespace-pre-wrap">{shareContent.share_text}</p>
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowUnlockModal(false)}>
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => confirmUnlock(pendingAction || 'linkedin_share')}
                disabled={unlocking}
              >
                {unlocking ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                Confirm & Unlock
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function ProfileEditor({ candidate, setCandidate, loadProfile, onAutoUnlock }) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 50, aspect: 1 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);

  // Helper to normalize education data from different formats
  const normalizeEducation = (eduList) => {
    if (!eduList || !Array.isArray(eduList)) return [];
    return eduList.map((edu, idx) => {
      if (typeof edu === 'string') {
        return { id: `edu-${idx}`, degree: edu, institution: '', year: '', field_of_study: '' };
      }
      return {
        id: edu.id || `edu-${idx}`,
        degree: edu.degree || edu.degree_type || edu.qualification || '',
        institution: edu.institution || edu.institution_name || edu.university || '',
        year: edu.year || edu.graduation_year || '',
        field_of_study: edu.field_of_study || edu.specialization || '',
        // Preserve original for saving back
        percentage_cgpa: edu.percentage_cgpa || '',
        board_university: edu.board_university || ''
      };
    });
  };

  // Helper to normalize work experience from different formats
  const normalizeWorkExperience = (expList) => {
    if (!expList || !Array.isArray(expList)) return [];
    return expList.map((exp, idx) => {
      if (typeof exp === 'string') {
        return { id: `exp-${idx}`, company: exp, designation: '', start_date: '', end_date: '', is_current: false };
      }
      return {
        id: exp.id || `exp-${idx}`,
        company: exp.company || exp.company_name || exp.organization || '',
        designation: exp.designation || exp.title || exp.role || exp.job_title || '',
        start_date: exp.start_date || exp.from_date || exp.joining_date || '',
        end_date: exp.end_date || exp.to_date || exp.leaving_date || (exp.is_current ? 'Present' : ''),
        is_current: exp.is_current || exp.current || exp.currently_working || false,
        description: exp.description || exp.responsibilities || exp.job_description || '',
        location: exp.location || ''
      };
    });
  };

  useEffect(() => {
    if (candidate) {
      // Get education and work experience from candidate or parsed_cv
      const rawEducation = candidate.education || candidate.parsed_cv?.education || [];
      const rawExperience = candidate.work_experience || candidate.parsed_cv?.work_experience || candidate.parsed_cv?.experience || [];
      
      setFormData({
        name: candidate.name || '',
        current_designation: candidate.current_designation || '',
        current_company: candidate.current_company || '',
        current_location: candidate.current_location || '',
        total_years_experience: candidate.total_years_experience || '',
        professional_summary: candidate.professional_summary || candidate.parsed_cv?.professional_summary || '',
        current_salary: candidate.current_salary || '',
        expected_salary: candidate.expected_salary || '',
        notice_period_days: candidate.notice_period_days || '',
        linkedin_url: candidate.linkedin_url || '',
        technical_skills: candidate.technical_skills || candidate.parsed_cv?.technical_skills || [],
        soft_skills: candidate.soft_skills || candidate.parsed_cv?.soft_skills || [],
        languages: candidate.languages || candidate.parsed_cv?.languages || [],
        certifications: (candidate.certifications || candidate.parsed_cv?.certifications || []).map(c => 
          typeof c === 'string' ? c : c?.certification_name || c?.name || ''
        ).filter(Boolean),
        education: normalizeEducation(rawEducation),
        work_experience: normalizeWorkExperience(rawExperience),
        actively_looking: candidate.actively_looking !== false,
        show_contact_info: candidate.show_contact_info || false,
        // New fields
        gender: candidate.gender || '',
        work_preference: candidate.work_preference || '',
        // Privacy settings
        show_photo_public: candidate.show_photo_public !== false,
        show_compensation_public: candidate.show_compensation_public || false,
        show_current_salary_public: candidate.show_current_salary_public || false,
        show_expected_salary_public: candidate.show_expected_salary_public || false,
        show_notice_period_public: candidate.show_notice_period_public || false,
        show_skills_public: candidate.show_skills_public !== false,
        show_education_public: candidate.show_education_public !== false,
        show_experience_public: candidate.show_experience_public !== false,
        show_certifications_public: candidate.show_certifications_public !== false,
        show_languages_public: candidate.show_languages_public !== false,
        show_summary_public: candidate.show_summary_public !== false,
        show_gender_public: candidate.show_gender_public !== false,
        show_work_preference_public: candidate.show_work_preference_public !== false,
        show_location_public: candidate.show_location_public !== false,
        show_designation_public: candidate.show_designation_public !== false,
        show_company_public: candidate.show_company_public !== false,
        show_experience_years_public: candidate.show_experience_years_public !== false,
        show_linkedin_public: candidate.show_linkedin_public !== false
      });
    }
  }, [candidate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const togglePrivacy = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('candidate_token');
      const res = await fetch(`${API_URL}/api/candidate/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }

      toast.success('Profile saved successfully!');
      loadProfile();
      
      // Check and trigger auto-unlocks based on what was saved
      if (onAutoUnlock) {
        const unlockChecks = [
          { field: 'technical_skills', minLength: 3, action: 'add_skills' },
          { field: 'education', minLength: 1, action: 'add_education' },
          { field: 'certifications', minLength: 1, action: 'add_certifications' },
          { field: 'languages', minLength: 1, action: 'add_languages' },
          { check: () => formData.current_designation && formData.current_location, action: 'add_profile_info' },
        ];
        
        for (const check of unlockChecks) {
          let shouldUnlock = false;
          if (check.field) {
            shouldUnlock = (formData[check.field]?.length || 0) >= check.minLength;
          } else if (check.check) {
            shouldUnlock = check.check();
          }
          if (shouldUnlock) {
            await onAutoUnlock(check.action);
          }
        }
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const getCroppedImg = async () => {
    if (!completedCrop || !imgRef.current) return null;

    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
    });
  };

  // Convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleCropSave = async () => {
    setUploading(true);
    try {
      const croppedBlob = await getCroppedImg();
      if (!croppedBlob) {
        toast.error('Please select an area to crop');
        setUploading(false);
        return;
      }

      // Convert to base64
      const base64Data = await blobToBase64(croppedBlob);
      
      const token = localStorage.getItem('candidate_token');
      const res = await fetch(`${API_URL}/api/candidate/photo-base64`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ image_data: base64Data })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Upload failed');
      }

      const data = await res.json();
      setCandidate(prev => ({ ...prev, profile_picture: data.url }));
      toast.success('Photo uploaded successfully!');
      setShowCropModal(false);
      setCropImage(null);
      loadProfile();
      
      // Trigger auto-unlock for adding profile picture
      if (onAutoUnlock) {
        await onAutoUnlock('add_picture');
      }
    } catch (err) {
      console.error('Photo upload error:', err);
      toast.error(err.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  // Work Experience handlers
  const addWorkExperience = () => {
    const newExp = {
      id: Date.now().toString(),
      company: '',
      designation: '',
      start_date: '',
      end_date: '',
      is_current: false,
      description: ''
    };
    handleChange('work_experience', [...(formData.work_experience || []), newExp]);
  };

  const updateWorkExperience = (index, field, value) => {
    const updated = [...(formData.work_experience || [])];
    updated[index] = { ...updated[index], [field]: value };
    handleChange('work_experience', updated);
  };

  const removeWorkExperience = (index) => {
    const updated = (formData.work_experience || []).filter((_, i) => i !== index);
    handleChange('work_experience', updated);
  };

  // Education handlers
  const addEducation = () => {
    const newEdu = {
      id: Date.now().toString(),
      degree: '',
      institution: '',
      year: '',
      field_of_study: ''
    };
    handleChange('education', [...(formData.education || []), newEdu]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...(formData.education || [])];
    updated[index] = { ...updated[index], [field]: value };
    handleChange('education', updated);
  };

  const removeEducation = (index) => {
    const updated = (formData.education || []).filter((_, i) => i !== index);
    handleChange('education', updated);
  };

  return (
    <div className="space-y-6">
      {/* Photo Upload with Crop */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <SectionHeader 
            icon={Camera} 
            title="Profile Photo" 
            isPublic={formData.show_photo_public} 
            onTogglePrivacy={() => togglePrivacy('show_photo_public')} 
          />
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="relative">
              <Avatar className="w-28 h-28 border-4 border-white shadow-lg">
                <AvatarImage src={candidate?.profile_picture?.startsWith('data:') || candidate?.profile_picture?.startsWith('http') ? candidate.profile_picture : (candidate?.profile_picture ? `${API_URL}${candidate.profile_picture}` : null)} />
                <AvatarFallback className="bg-gradient-to-br from-slate-700 to-slate-800 text-white text-2xl font-bold">
                  {getInitials(candidate?.name)}
                </AvatarFallback>
              </Avatar>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute -bottom-2 -right-2 bg-blue-600 text-white p-2.5 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">{formatName(candidate?.name)}</h2>
              <p className="text-slate-600">{candidate?.current_designation || 'Add your title'}</p>
              {candidate?.phone_verified && (
                <Badge className="mt-2 bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              )}
              <p className="text-xs text-slate-400 mt-2">Click camera to upload & crop photo</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Privacy Settings */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Visibility Settings
          </CardTitle>
          <CardDescription>Control what recruiters can see on your public profile</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <Label>Show Contact Information</Label>
              <p className="text-sm text-slate-500">Display phone and email publicly</p>
            </div>
            <Switch
              checked={formData.show_contact_info}
              onCheckedChange={(checked) => handleChange('show_contact_info', checked)}
            />
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <Label>Actively Looking for Jobs</Label>
              <p className="text-sm text-slate-500">Show "Open to Work" badge</p>
            </div>
            <Switch
              checked={formData.actively_looking}
              onCheckedChange={(checked) => handleChange('actively_looking', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Advanced Privacy Controls */}
      <PrivacyControlsCard candidate={candidate} loadProfile={loadProfile} />

      {/* Basic Info */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <SectionHeader icon={User} title="Basic Information" showToggle={false} />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Full Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Your full name"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Gender</Label>
                <PrivacyToggle 
                  isPublic={formData.show_gender_public} 
                  onToggle={() => togglePrivacy('show_gender_public')} 
                />
              </div>
              <select
                value={formData.gender || ''}
                onChange={(e) => handleChange('gender', e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Current Location</Label>
                <PrivacyToggle 
                  isPublic={formData.show_location_public} 
                  onToggle={() => togglePrivacy('show_location_public')} 
                />
              </div>
              <MasterSelect
                masterDb="locations"
                selected={formData.current_location ? [formData.current_location] : []}
                onChange={(locs) => handleChange('current_location', locs[0] || '')}
                placeholder="Select location..."
                allowCustom={true}
                maxSelections={1}
                tagVariant="language"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Work Preference</Label>
                <PrivacyToggle 
                  isPublic={formData.show_work_preference_public} 
                  onToggle={() => togglePrivacy('show_work_preference_public')} 
                />
              </div>
              <select
                value={formData.work_preference || ''}
                onChange={(e) => handleChange('work_preference', e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Work Preference</option>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
                <option value="office">Office / On-site</option>
                <option value="flexible">Flexible</option>
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Current Designation</Label>
                <PrivacyToggle 
                  isPublic={formData.show_designation_public} 
                  onToggle={() => togglePrivacy('show_designation_public')} 
                />
              </div>
              <Input
                value={formData.current_designation}
                onChange={(e) => handleChange('current_designation', e.target.value)}
                placeholder="e.g. Senior Software Engineer"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Current Company</Label>
                <PrivacyToggle 
                  isPublic={formData.show_company_public} 
                  onToggle={() => togglePrivacy('show_company_public')} 
                />
              </div>
              <Input
                value={formData.current_company}
                onChange={(e) => handleChange('current_company', e.target.value)}
                placeholder="Company name"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Total Experience (years)</Label>
                <PrivacyToggle 
                  isPublic={formData.show_experience_years_public} 
                  onToggle={() => togglePrivacy('show_experience_years_public')} 
                />
              </div>
              <Input
                type="number"
                value={formData.total_years_experience}
                onChange={(e) => handleChange('total_years_experience', e.target.value)}
                placeholder="0"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>LinkedIn Profile</Label>
                <PrivacyToggle 
                  isPublic={formData.show_linkedin_public} 
                  onToggle={() => togglePrivacy('show_linkedin_public')} 
                />
              </div>
              <Input
                value={formData.linkedin_url}
                onChange={(e) => handleChange('linkedin_url', e.target.value)}
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Professional Summary */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <SectionHeader 
            icon={FileText} 
            title="Professional Summary" 
            isPublic={formData.show_summary_public} 
            onTogglePrivacy={() => togglePrivacy('show_summary_public')} 
          />
        </CardHeader>
        <CardContent>
          <Textarea
            value={formData.professional_summary}
            onChange={(e) => handleChange('professional_summary', e.target.value)}
            placeholder="Tell us about yourself, your experience, and career goals..."
            rows={5}
          />
        </CardContent>
      </Card>

      {/* Compensation & Notice - Individual Privacy Toggles */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-blue-600" />
            Compensation & Notice
          </CardTitle>
          <CardDescription>Each field can be made public or private individually</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-medium">Current CTC (Annual)</Label>
                <PrivacyToggle 
                  isPublic={formData.show_current_salary_public} 
                  onToggle={() => togglePrivacy('show_current_salary_public')} 
                />
              </div>
              <Input
                value={formData.current_salary}
                onChange={(e) => handleChange('current_salary', e.target.value)}
                placeholder="e.g. 1500000"
              />
              {!formData.show_current_salary_public && (
                <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Hidden from public profile
                </p>
              )}
            </div>

            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-medium">Expected CTC (Annual)</Label>
                <PrivacyToggle 
                  isPublic={formData.show_expected_salary_public} 
                  onToggle={() => togglePrivacy('show_expected_salary_public')} 
                />
              </div>
              <Input
                value={formData.expected_salary}
                onChange={(e) => handleChange('expected_salary', e.target.value)}
                placeholder="e.g. 2000000"
              />
              {!formData.show_expected_salary_public && (
                <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Hidden from public profile
                </p>
              )}
            </div>

            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-medium">Notice Period (days)</Label>
                <PrivacyToggle 
                  isPublic={formData.show_notice_period_public} 
                  onToggle={() => togglePrivacy('show_notice_period_public')} 
                />
              </div>
              <Input
                type="number"
                value={formData.notice_period_days}
                onChange={(e) => handleChange('notice_period_days', e.target.value)}
                placeholder="30"
              />
              {!formData.show_notice_period_public && (
                <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Hidden from public profile
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Work Experience */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <SectionHeader 
            icon={Building2} 
            title="Work Experience" 
            isPublic={formData.show_experience_public} 
            onTogglePrivacy={() => togglePrivacy('show_experience_public')} 
          />
        </CardHeader>
        <CardContent className="space-y-4">
          {(formData.work_experience || []).map((exp, idx) => (
            <div key={exp.id || idx} className="p-4 bg-slate-50 rounded-lg relative">
              <button
                onClick={() => removeWorkExperience(idx)}
                className="absolute top-2 right-2 p-1 text-red-500 hover:bg-red-100 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Company</Label>
                  <Input
                    value={exp.company || ''}
                    onChange={(e) => updateWorkExperience(idx, 'company', e.target.value)}
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <Label>Designation</Label>
                  <Input
                    value={exp.designation || exp.title || ''}
                    onChange={(e) => updateWorkExperience(idx, 'designation', e.target.value)}
                    placeholder="Job title"
                  />
                </div>
                <div>
                  <Label>Start Date</Label>
                  <Input
                    value={exp.start_date || ''}
                    onChange={(e) => updateWorkExperience(idx, 'start_date', e.target.value)}
                    placeholder="e.g. Jan 2020"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    value={exp.is_current ? 'Present' : (exp.end_date || '')}
                    onChange={(e) => updateWorkExperience(idx, 'end_date', e.target.value)}
                    placeholder="e.g. Dec 2023 or Present"
                    disabled={exp.is_current}
                  />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Switch
                  checked={exp.is_current || false}
                  onCheckedChange={(checked) => updateWorkExperience(idx, 'is_current', checked)}
                />
                <Label className="text-sm">Currently working here</Label>
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addWorkExperience} className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Add Work Experience
          </Button>
        </CardContent>
      </Card>

      {/* Education */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <SectionHeader 
            icon={GraduationCap} 
            title="Education" 
            isPublic={formData.show_education_public} 
            onTogglePrivacy={() => togglePrivacy('show_education_public')} 
          />
        </CardHeader>
        <CardContent className="space-y-4">
          {(formData.education || []).map((edu, idx) => (
            <div key={edu.id || idx} className="p-4 bg-slate-50 rounded-lg relative">
              <button
                onClick={() => removeEducation(idx)}
                className="absolute top-2 right-2 p-1 text-red-500 hover:bg-red-100 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Degree</Label>
                  <Input
                    value={edu.degree || edu.qualification || ''}
                    onChange={(e) => updateEducation(idx, 'degree', e.target.value)}
                    placeholder="e.g. Bachelor of Technology"
                  />
                </div>
                <div>
                  <Label>Institution</Label>
                  <Input
                    value={edu.institution || edu.university || ''}
                    onChange={(e) => updateEducation(idx, 'institution', e.target.value)}
                    placeholder="University/College name"
                  />
                </div>
                <div>
                  <Label>Field of Study</Label>
                  <Input
                    value={edu.field_of_study || edu.specialization || ''}
                    onChange={(e) => updateEducation(idx, 'field_of_study', e.target.value)}
                    placeholder="e.g. Computer Science"
                  />
                </div>
                <div>
                  <Label>Year</Label>
                  <Input
                    value={edu.year || edu.graduation_year || ''}
                    onChange={(e) => updateEducation(idx, 'year', e.target.value)}
                    placeholder="e.g. 2020"
                  />
                </div>
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addEducation} className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Add Education
          </Button>
        </CardContent>
      </Card>

      {/* Skills */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <SectionHeader 
            icon={Code} 
            title="Skills" 
            isPublic={formData.show_skills_public} 
            onTogglePrivacy={() => togglePrivacy('show_skills_public')} 
          />
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Technical Skills</Label>
            <MasterSelect
              masterDb="skills"
              selected={formData.technical_skills || []}
              onChange={(skills) => handleChange('technical_skills', skills)}
              placeholder="Search or add skills..."
              allowCustom={true}
              tagVariant="skill"
            />
          </div>
          <div>
            <Label>Soft Skills</Label>
            <TagInput
              value={formData.soft_skills || []}
              onChange={(skills) => handleChange('soft_skills', skills)}
              placeholder="Type and press Enter..."
              tagVariant="secondary"
            />
          </div>
        </CardContent>
      </Card>

      {/* Languages */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <SectionHeader 
            icon={Languages} 
            title="Languages" 
            isPublic={formData.show_languages_public} 
            onTogglePrivacy={() => togglePrivacy('show_languages_public')} 
          />
        </CardHeader>
        <CardContent>
          <MasterSelect
            masterDb="languages"
            selected={formData.languages || []}
            onChange={(langs) => handleChange('languages', langs)}
            placeholder="Search or add languages..."
            allowCustom={true}
            tagVariant="language"
          />
        </CardContent>
      </Card>

      {/* Certifications */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <SectionHeader 
            icon={Award} 
            title="Certifications" 
            isPublic={formData.show_certifications_public} 
            onTogglePrivacy={() => togglePrivacy('show_certifications_public')} 
          />
        </CardHeader>
        <CardContent>
          <TagInput
            value={formData.certifications || []}
            onChange={(certs) => handleChange('certifications', certs)}
            placeholder="Type certification name and press Enter..."
            tagVariant="colorful"
          />
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={loadProfile}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Reset
        </Button>
        <Button onClick={handleSave} disabled={saving} className="px-8 bg-blue-600 hover:bg-blue-700">
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      {/* Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Crop Profile Photo</CardTitle>
              <CardDescription>Drag to select the area you want to use</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="max-h-96 overflow-auto bg-slate-100 rounded-lg p-2">
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={1}
                  circularCrop
                >
                  <img
                    ref={imgRef}
                    src={cropImage}
                    alt="Crop preview"
                    style={{ maxWidth: '100%' }}
                  />
                </ReactCrop>
              </div>
              <div className="flex justify-end gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => { setShowCropModal(false); setCropImage(null); }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCropSave} 
                  disabled={uploading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Save Photo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// Salary History Editor Component - Master Summary Table
function SalaryHistoryEditor({ candidate, setCandidate, loadProfile, onAutoUnlock }) {
  const [salaryHistory, setSalaryHistory] = useState(candidate?.salary_progression || []);
  const [saving, setSaving] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  
  // New entry form
  const [newEntry, setNewEntry] = useState({
    year: new Date().getFullYear(),
    company: '',
    role: '',
    estimated_annual_ctc: ''
  });

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const handleAddEntry = () => {
    if (!newEntry.company || !newEntry.role || !newEntry.estimated_annual_ctc) {
      toast.error('Please fill all fields');
      return;
    }
    
    const entry = {
      ...newEntry,
      estimated_annual_ctc: parseInt(newEntry.estimated_annual_ctc) || 0
    };
    
    setSalaryHistory([...salaryHistory, entry].sort((a, b) => a.year - b.year));
    setNewEntry({ year: new Date().getFullYear(), company: '', role: '', estimated_annual_ctc: '' });
    toast.success('Entry added');
  };

  const handleUpdateEntry = (idx, field, value) => {
    const updated = [...salaryHistory];
    updated[idx] = { ...updated[idx], [field]: field === 'estimated_annual_ctc' ? parseInt(value) || 0 : value };
    setSalaryHistory(updated);
  };

  const handleDeleteEntry = (idx) => {
    setSalaryHistory(salaryHistory.filter((_, i) => i !== idx));
    toast.success('Entry removed');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('candidate_token');
      const response = await fetch(`${API_URL}/api/candidate/update-salary-history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ salary_progression: salaryHistory })
      });
      
      if (!response.ok) throw new Error('Failed to save');
      
      toast.success('Salary history saved!');
      loadProfile();
      
      // Trigger auto-unlock for adding compensation info
      if (salaryHistory.length > 0 && onAutoUnlock) {
        await onAutoUnlock('add_compensation');
      }
    } catch (error) {
      toast.error('Failed to save salary history');
    } finally {
      setSaving(false);
    }
  };

  const maxCtc = salaryHistory.length > 0 ? Math.max(...salaryHistory.map(e => e.estimated_annual_ctc || 0)) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Salary History</h2>
          <p className="text-sm text-slate-500">Edit your career salary progression. This helps us estimate your current CTC accurately.</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* Current Salary Graph Preview */}
      {salaryHistory.length > 0 && (
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              Salary Progression Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {salaryHistory.map((entry, idx) => {
                const pct = maxCtc > 0 ? ((entry.estimated_annual_ctc || 0) / maxCtc) * 100 : 0;
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-10 text-right font-mono">{entry.year}</span>
                    <div className="flex-1">
                      <div className="h-8 bg-slate-100 rounded-md overflow-hidden relative">
                        <div 
                          className="h-full rounded-md"
                          style={{ 
                            width: `${Math.max(pct, 15)}%`,
                            background: `linear-gradient(90deg, #34d399 0%, #059669 100%)`
                          }}
                        />
                        <span className="absolute inset-0 flex items-center px-3 text-xs font-medium text-slate-700 truncate">
                          {entry.company} - {entry.role} ({formatCurrency(entry.estimated_annual_ctc)})
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Editable Table */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">Edit Salary History</CardTitle>
          <CardDescription>Add, edit or remove your past salary records</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Existing Entries */}
          {salaryHistory.length > 0 ? (
            <div className="space-y-3">
              {salaryHistory.map((entry, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center p-3 bg-slate-50 rounded-lg">
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Year</Label>
                    <Input
                      type="number"
                      value={entry.year}
                      onChange={(e) => handleUpdateEntry(idx, 'year', parseInt(e.target.value))}
                      className="h-9 text-sm"
                      min="1990"
                      max="2030"
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs text-slate-500">Company</Label>
                    <Input
                      value={entry.company}
                      onChange={(e) => handleUpdateEntry(idx, 'company', e.target.value)}
                      className="h-9 text-sm"
                      placeholder="Company name"
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs text-slate-500">Role</Label>
                    <Input
                      value={entry.role}
                      onChange={(e) => handleUpdateEntry(idx, 'role', e.target.value)}
                      className="h-9 text-sm"
                      placeholder="Job title"
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs text-slate-500">Annual CTC (₹)</Label>
                    <Input
                      type="number"
                      value={entry.estimated_annual_ctc}
                      onChange={(e) => handleUpdateEntry(idx, 'estimated_annual_ctc', e.target.value)}
                      className="h-9 text-sm"
                      placeholder="e.g. 1200000"
                    />
                  </div>
                  <div className="col-span-1 flex items-end justify-center pb-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteEntry(idx)}
                      className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <TrendingUp className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No salary history yet. Add your first entry below.</p>
            </div>
          )}

          {/* Add New Entry Form */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add New Entry
            </h4>
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-2">
                <Label className="text-xs">Year</Label>
                <Input
                  type="number"
                  value={newEntry.year}
                  onChange={(e) => setNewEntry({ ...newEntry, year: parseInt(e.target.value) })}
                  className="h-9 text-sm"
                  min="1990"
                  max="2030"
                />
              </div>
              <div className="col-span-3">
                <Label className="text-xs">Company</Label>
                <Input
                  value={newEntry.company}
                  onChange={(e) => setNewEntry({ ...newEntry, company: e.target.value })}
                  className="h-9 text-sm"
                  placeholder="Company name"
                />
              </div>
              <div className="col-span-3">
                <Label className="text-xs">Role</Label>
                <Input
                  value={newEntry.role}
                  onChange={(e) => setNewEntry({ ...newEntry, role: e.target.value })}
                  className="h-9 text-sm"
                  placeholder="Job title"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Annual CTC (₹)</Label>
                <Input
                  type="number"
                  value={newEntry.estimated_annual_ctc}
                  onChange={(e) => setNewEntry({ ...newEntry, estimated_annual_ctc: e.target.value })}
                  className="h-9 text-sm"
                  placeholder="1200000"
                />
              </div>
              <div className="col-span-2">
                <Button onClick={handleAddEntry} className="w-full h-9">
                  <Plus className="w-4 h-4 mr-1" />Add
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Why is this important?</p>
            <p className="mt-1">Your salary history helps us accurately estimate your current CTC and show your career progression to potential employers. If the AI-estimated values are incorrect, please update them here.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Job Search Component
function JobSearchPage({ candidate }) {
  const [filters, setFilters] = useState({
    skills: [],
    companies: [],
    locations: []
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    if (filters.skills.length === 0 && filters.companies.length === 0 && filters.locations.length === 0) {
      toast.error('Please select at least one filter');
      return;
    }

    setLoading(true);
    setHasSearched(true);
    try {
      const token = localStorage.getItem('candidate_token');
      const params = new URLSearchParams();
      
      if (filters.skills.length > 0) params.append('skills', filters.skills.join(','));
      if (filters.companies.length > 0) params.append('companies', filters.companies.join(','));
      if (filters.locations.length > 0) params.append('locations', filters.locations.join(','));

      const res = await fetch(`${API_URL}/api/candidate/search-jobs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Search failed');

      const data = await res.json();
      setSearchResults(data.jobs || []);
      
      if (data.jobs?.length === 0) {
        toast.info('No jobs found matching your criteria');
      } else {
        toast.success(`Found ${data.jobs.length} jobs`);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to search jobs');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ skills: [], companies: [], locations: [] });
    setSearchResults([]);
    setHasSearched(false);
  };

  return (
    <div className="space-y-6">
      {/* Search Filters */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-blue-600" />
            Find Jobs
          </CardTitle>
          <CardDescription>Search by skills, companies, or locations from our database</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="mb-2 block">Skills</Label>
              <MasterSelect
                masterDb="skills"
                selected={filters.skills}
                onChange={(skills) => setFilters(prev => ({ ...prev, skills }))}
                placeholder="Select skills..."
                allowCustom={false}
                tagVariant="skill"
              />
            </div>
            <div>
              <Label className="mb-2 block">Companies</Label>
              <MasterSelect
                masterDb="companies"
                selected={filters.companies}
                onChange={(companies) => setFilters(prev => ({ ...prev, companies }))}
                placeholder="Select companies..."
                allowCustom={false}
                tagVariant="secondary"
              />
            </div>
            <div>
              <Label className="mb-2 block">Locations</Label>
              <MasterSelect
                masterDb="locations"
                selected={filters.locations}
                onChange={(locations) => setFilters(prev => ({ ...prev, locations }))}
                placeholder="Select locations..."
                allowCustom={false}
                tagVariant="language"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={handleSearch} 
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Search Jobs
            </Button>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {hasSearched && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-800">
            {searchResults.length > 0 
              ? `Found ${searchResults.length} job${searchResults.length !== 1 ? 's' : ''}` 
              : 'No results'}
          </h3>
          
          {searchResults.length > 0 ? (
            searchResults.map((job, idx) => (
              <Card key={idx} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-slate-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800">{job.title || job.job_title}</h4>
                        <p className="text-sm text-slate-600">{job.company || job.company_name}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {job.location && (
                            <Badge variant="outline" className="text-xs">
                              <MapPin className="w-3 h-3 mr-1" />
                              {job.location}
                            </Badge>
                          )}
                          {job.experience && (
                            <Badge variant="outline" className="text-xs">
                              <Briefcase className="w-3 h-3 mr-1" />
                              {job.experience}
                            </Badge>
                          )}
                        </div>
                        {job.skills && job.skills.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {job.skills.slice(0, 5).map((skill, i) => (
                              <Badge key={i} className="text-xs bg-blue-50 text-blue-700">
                                {skill}
                              </Badge>
                            ))}
                            {job.skills.length > 5 && (
                              <Badge className="text-xs bg-slate-100 text-slate-600">
                                +{job.skills.length - 5} more
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      {job.match_score && (
                        <Badge className={`${
                          job.match_score >= 80 ? 'bg-green-100 text-green-700' :
                          job.match_score >= 60 ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {job.match_score}% Match
                        </Badge>
                      )}
                      {job.posted_date && (
                        <p className="text-xs text-slate-400 mt-2">{job.posted_date}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="border-0 shadow-md">
              <CardContent className="p-12 text-center">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-600">No jobs found</h3>
                <p className="text-sm text-slate-500 mt-2">Try different search criteria</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Initial State */}
      {!hasSearched && (
        <Card className="border-0 shadow-md">
          <CardContent className="p-12 text-center">
            <Target className="w-12 h-12 text-blue-200 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-600">Start Your Job Search</h3>
            <p className="text-sm text-slate-500 mt-2">
              Use the filters above to find jobs matching your skills and preferences
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Settings Component
function SettingsPage({ candidate, loadProfile }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState(candidate?.username || '');
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [saving, setSaving] = useState(false);

  // Check username availability
  const checkUsername = async (value) => {
    if (!value || value.length < 3) {
      setUsernameAvailable(null);
      return;
    }

    // Clean username
    const cleaned = value.toLowerCase().replace(/[^a-z0-9-_]/g, '');
    setUsername(cleaned);

    setCheckingUsername(true);
    try {
      const token = localStorage.getItem('candidate_token');
      const res = await fetch(`${API_URL}/api/candidate/check-username?username=${cleaned}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setUsernameAvailable(data.available);
    } catch (e) {
      console.error('Error checking username:', e);
    } finally {
      setCheckingUsername(false);
    }
  };

  const saveUsername = async () => {
    if (!usernameAvailable) {
      toast.error('Please choose an available username');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('candidate_token');
      const res = await fetch(`${API_URL}/api/candidate/username`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ username })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to update username');
      }

      toast.success('Username updated successfully!');
      loadProfile();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('candidate_token');
    localStorage.removeItem('candidate_user');
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="space-y-6">
      {/* Username Settings */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AtSign className="w-5 h-5 text-blue-600" />
            Profile URL
          </CardTitle>
          <CardDescription>
            Customize your public profile link
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Username</Label>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-slate-500">daruwallas.com/</span>
              <div className="relative flex-1">
                <Input
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    checkUsername(e.target.value);
                  }}
                  placeholder="your-username"
                  className={`${
                    usernameAvailable === true ? 'border-green-500 focus:ring-green-500' :
                    usernameAvailable === false ? 'border-red-500 focus:ring-red-500' : ''
                  }`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checkingUsername && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                  {!checkingUsername && usernameAvailable === true && (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  )}
                  {!checkingUsername && usernameAvailable === false && (
                    <X className="w-4 h-4 text-red-500" />
                  )}
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Only letters, numbers, hyphens and underscores allowed. Username can be changed once every 14 days or 3 times in 6 months.
            </p>
            {candidate?.last_username_change && (
              <p className="text-xs text-amber-600 mt-1">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                Last changed: {new Date(candidate.last_username_change).toLocaleDateString()}
              </p>
            )}
          </div>

          <Button onClick={saveUsername} disabled={!usernameAvailable || saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Update Username
          </Button>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-800">Phone Number</p>
              <p className="text-sm text-slate-500">{candidate?.phone}</p>
            </div>
            {candidate?.phone_verified && (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle className="w-3 h-3 mr-1" />
                Verified
              </Badge>
            )}
          </div>
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-800">Email</p>
              <p className="text-sm text-slate-500">{candidate?.email || 'Not set'}</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-800">Profile Created</p>
              <p className="text-sm text-slate-500">
                {candidate?.created_at ? new Date(candidate.created_at).toLocaleDateString() : 'Unknown'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Privacy Controls - Per-Field Toggles */}
      <PrivacyControlsCard candidate={candidate} loadProfile={loadProfile} />

      {/* Logout */}
      <Card className="border-0 shadow-md border-red-200">
        <CardContent className="p-6">
          <Button onClick={handleLogout} variant="outline" className="text-red-600 border-red-300 hover:bg-red-50">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Main Portal Component
export default function CandidatePortal() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [candidate, setCandidate] = useState(null);
  const [matches, setMatches] = useState([]);
  const [pageSettings, setPageSettings] = useState(null);
  
  // Auth check
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Login state
  const [loginPhone, setLoginPhone] = useState('');
  const [loginOtp, setLoginOtp] = useState('');
  const [loginStep, setLoginStep] = useState('phone');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeUrl, setResumeUrl] = useState(null);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [showResumeSharePrompt, setShowResumeSharePrompt] = useState(false);
  const resumeInputRef = useRef(null);

  // Load page settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/settings`);
        if (res.ok) {
          const data = await res.json();
          setPageSettings(data);
          // Apply title and favicon
          if (data.portal_page_title) {
            document.title = data.portal_page_title;
          }
          if (data.favicon_url) {
            const favicon = document.querySelector("link[rel*='icon']") || document.createElement('link');
            favicon.type = 'image/x-icon';
            favicon.rel = 'shortcut icon';
            favicon.href = data.favicon_url;
            document.head.appendChild(favicon);
          }
        }
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
    };
    loadSettings();
  }, []);

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('candidate_token');
    if (token) {
      loadProfile(token);
    } else {
      setAuthLoading(false);
    }
  }, []);

  const loadProfile = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/candidate/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        localStorage.removeItem('candidate_token');
        setAuthLoading(false);
        return;
      }

      const data = await res.json();
      setCandidate(data);
      setIsAuthenticated(true);
      
      // Load matches
      loadMatches(token);
    } catch (err) {
      console.error('Auth error:', err);
      localStorage.removeItem('candidate_token');
    } finally {
      setLoading(false);
      setAuthLoading(false);
    }
  };

  const loadMatches = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/candidate/matches?limit=20`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('candidate_token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Combine internal matches and AI search results
        const allMatches = [...(data.matches || []), ...(data.search_results || [])];
        setMatches(allMatches);
      }
    } catch (e) {
      console.error('Error loading matches:', e);
    }
  };

  // Handle login - send OTP
  const handleSendLoginOtp = async () => {
    if (!loginPhone || loginPhone.length < 10) {
      setLoginError('Please enter a valid phone number');
      return;
    }

    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/candidate/login/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: loginPhone })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send OTP');
      }

      // Show success message - OTP sent via WhatsApp
      toast.success('OTP sent to your WhatsApp!');
      setLoginStep('otp');
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle login - verify OTP
  const handleVerifyLoginOtp = async () => {
    if (!loginOtp || loginOtp.length !== 6) {
      setLoginError('Please enter the 6-digit OTP');
      return;
    }

    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/candidate/login/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: loginPhone, otp: loginOtp })
      });

      let data;
      try {
        data = await res.json();
      } catch (jsonErr) {
        throw new Error('Invalid OTP');
      }

      if (!res.ok) {
        throw new Error(data.detail || 'Invalid OTP');
      }

      localStorage.setItem('candidate_token', data.access_token);
      localStorage.setItem('candidate_user', JSON.stringify(data.user));
      
      toast.success('Login successful!');
      setIsAuthenticated(true);
      loadProfile(data.access_token);
    } catch (err) {
      setLoginError(err.message);
      setLoginOtp('');
    } finally {
      setLoginLoading(false);
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse">
          <img 
            src="/images/daruwallas-logo.png" 
            alt="Daruwallas" 
            className="w-20 h-20 object-contain opacity-50"
          />
        </div>
      </div>
    );
  }

  // Login screen - Enhanced visually stunning design
  if (!isAuthenticated) {
    // OTP Step - Verify OTP
    if (loginStep === 'otp') {
      return (
        <div className="min-h-screen h-screen flex flex-col lg:flex-row">
          {/* Left side - Branding */}
          <div 
            className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' }}
          >
            {/* Decorative gradient orb */}
            <div 
              className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(201, 165, 78, 0.2) 0%, rgba(201, 165, 78, 0.05) 40%, transparent 70%)',
              }}
            />
            
            <div className="relative z-10 flex flex-col justify-center items-center w-full px-12">
              <img 
                src="/images/daruwallas-logo.png" 
                alt="Daruwallas" 
                className="w-32 h-32 object-contain mb-8"
              />
              <h2 className="text-3xl font-semibold text-white mb-4 text-center">
                Welcome Back
              </h2>
              <p className="text-gray-400 text-center max-w-sm leading-relaxed">
                Access your profile, view job matches, and manage your career journey all in one place.
              </p>
              
              {/* Trust indicators */}
              <div className="mt-16 space-y-4">
                <div className="flex items-center gap-3 text-gray-400">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#C9A54E]" />
                  </div>
                  <span className="text-sm">Secure WhatsApp verification</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                    <Target className="w-5 h-5 text-[#C9A54E]" />
                  </div>
                  <span className="text-sm">AI-powered job matching</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-[#C9A54E]" />
                  </div>
                  <span className="text-sm">Profile visibility to top recruiters</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - OTP Form */}
          <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 bg-white">
            <div className="w-full max-w-sm">
              {/* Mobile logo */}
              <div className="lg:hidden text-center mb-10">
                <img 
                  src="/images/daruwallas-logo.png" 
                  alt="Daruwallas" 
                  className="w-20 h-20 object-contain mx-auto mb-4"
                />
              </div>

              {/* Back Button */}
              <button
                onClick={() => { setLoginStep('phone'); setLoginOtp(''); setLoginError(''); }}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-8"
                data-testid="back-to-phone"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                <span className="text-sm">Back</span>
              </button>

              {/* Header */}
              <div className="mb-10">
                <h1 className="text-3xl font-semibold text-gray-900 mb-3">Verify OTP</h1>
                <p className="text-gray-500">
                  Enter the 6-digit code sent to
                </p>
                <p className="text-gray-900 font-medium mt-1 text-lg">
                  +91-{loginPhone.replace(/\D/g, '')}
                </p>
              </div>

              {loginError && (
                <div className="flex items-center gap-2 p-4 text-sm text-red-600 bg-red-50 rounded-xl mb-6">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{loginError}</span>
                </div>
              )}

              {/* OTP Input */}
              <div className="mb-8">
                <input
                  type="text"
                  value={loginOtp}
                  onChange={(e) => setLoginOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full h-16 text-3xl text-center tracking-[0.5em] font-semibold border-2 border-gray-200 rounded-xl focus:border-[#C9A54E] focus:ring-4 focus:ring-[#C9A54E]/10 focus:outline-none transition-all"
                  maxLength={6}
                  data-testid="otp-input"
                />
                <p className="text-sm text-gray-400 mt-4 text-center">
                  Check your WhatsApp for the OTP
                </p>
              </div>

              <button
                onClick={handleVerifyLoginOtp}
                disabled={loginLoading || loginOtp.length !== 6}
                className="w-full h-14 rounded-xl text-base font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #C9A54E 0%, #B8943D 100%)' }}
                data-testid="verify-otp-btn"
              >
                {loginLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Login'}
              </button>

              {/* Resend OTP */}
              <div className="text-center mt-6">
                <button
                  onClick={handleSendLoginOtp}
                  className="text-sm text-gray-500 hover:text-[#C9A54E] transition-colors"
                  disabled={loginLoading}
                >
                  Didn't receive OTP? <span className="font-medium">Resend</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Phone Step - Enter Phone Number (Completely Redesigned)
    return (
      <div className="min-h-screen h-screen flex flex-col lg:flex-row bg-gradient-to-br from-slate-50 to-white overflow-hidden relative">
        {/* Diagonal Hashtag Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none select-none z-0">
          {/* Top-right to bottom-left diagonal text */}
          <div 
            className="absolute text-slate-100 font-bold whitespace-nowrap"
            style={{
              fontSize: '6rem',
              transform: 'rotate(-45deg)',
              top: '-5%',
              right: '-10%',
              opacity: 0.5
            }}
          >
            #YourCareer #GetMatched #TopJobs
          </div>
          <div 
            className="absolute text-slate-100 font-bold whitespace-nowrap"
            style={{
              fontSize: '4rem',
              transform: 'rotate(-45deg)',
              top: '25%',
              right: '-20%',
              opacity: 0.4
            }}
          >
            #AIMatching #SmartHiring #TalentFirst
          </div>
          <div 
            className="absolute text-slate-100 font-bold whitespace-nowrap"
            style={{
              fontSize: '5rem',
              transform: 'rotate(-45deg)',
              bottom: '10%',
              right: '-15%',
              opacity: 0.3
            }}
          >
            #DreamJob #CareerGrowth #Opportunities
          </div>
          
          {/* Bottom-left to top-right diagonal text */}
          <div 
            className="absolute text-slate-100/40 font-bold whitespace-nowrap"
            style={{
              fontSize: '5rem',
              transform: 'rotate(45deg)',
              bottom: '-5%',
              left: '-15%',
              opacity: 0.4
            }}
          >
            #TopTalent #HireRight #Success
          </div>
          <div 
            className="absolute text-slate-100/30 font-bold whitespace-nowrap"
            style={{
              fontSize: '3.5rem',
              transform: 'rotate(45deg)',
              top: '60%',
              left: '-25%',
              opacity: 0.3
            }}
          >
            #NextStep #FindJobs #ApplyNow
          </div>
        </div>

        {/* Main Content - Centered */}
        <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 relative z-10">
          <div className="w-full max-w-md">
            {/* Logo - Centered */}
            <div className="text-center mb-12">
              <img 
                src={pageSettings?.company_logo_url || "/images/daruwallas-logo.png"}
                alt={pageSettings?.company_name || "Daruwallas"} 
                className="h-20 md:h-24 lg:h-28 object-contain mx-auto mb-6"
                onError={(e) => { e.target.src = "/images/daruwallas-logo.png"; }}
              />
              <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">
                Candidate Portal
              </h1>
              <p className="text-slate-500 text-base md:text-lg max-w-sm mx-auto">
                Access your profile, view AI-matched jobs, and accelerate your career
              </p>
            </div>

            {/* Login Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8 md:p-10">
              <div className="mb-8">
                <p 
                  className="text-xs font-semibold tracking-[0.2em] uppercase mb-2"
                  style={{ color: '#C9A54E' }}
                >
                  SECURE LOGIN
                </p>
                <h2 className="text-2xl font-semibold text-gray-900">
                  Welcome back
                </h2>
              </div>

              {loginError && (
                <div className="flex items-center gap-2 p-4 text-sm text-red-600 bg-red-50 rounded-xl mb-6">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{loginError}</span>
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); handleSendLoginOtp(); }} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="tel"
                      value={loginPhone}
                      onChange={(e) => setLoginPhone(e.target.value)}
                      placeholder="Enter your phone number"
                      className="w-full pl-12 pr-4 py-4 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A54E] focus:border-transparent text-lg"
                      disabled={loginLoading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loginLoading || !loginPhone.trim()}
                  className="w-full h-14 text-lg font-medium rounded-xl"
                  style={{ backgroundColor: '#C9A54E' }}
                >
                  {loginLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="w-5 h-5 mr-2" />
                      Get WhatsApp OTP
                    </>
                  )}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-slate-400">or</span>
                </div>
              </div>

              {/* New User Link */}
              <p className="text-center text-slate-500">
                New here?{' '}
                <a href="/" className="font-medium hover:underline" style={{ color: '#C9A54E' }}>
                  Upload your CV to get started
                </a>
              </p>
            </div>

            {/* Trust Badges - Horizontal */}
            <div className="mt-10 flex flex-wrap justify-center gap-4 md:gap-8">
              <div className="flex items-center gap-2 text-slate-500">
                <Shield className="w-5 h-5 text-green-500" />
                <span className="text-sm">Secure</span>
              </div>
              <div className="flex items-center gap-2 text-slate-500">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <span className="text-sm">AI-Powered</span>
              </div>
              <div className="flex items-center gap-2 text-slate-500">
                <Target className="w-5 h-5 text-blue-500" />
                <span className="text-sm">Smart Matching</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated view
  const activeTab = location.pathname.split('/')[2] || 'dashboard';

  const handleViewResume = () => {
    if (candidate?.original_file_name) {
      const token = localStorage.getItem('candidate_token');
      setResumeUrl(`${API_URL}/api/candidate/resume?t=${Date.now()}`);
      setShowResumeModal(true);
    } else {
      toast.error('No resume found. Please upload one.');
    }
  };

  const handleUploadResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowed = ['.pdf', '.docx', '.doc', '.txt'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowed.includes(ext)) {
      toast.error('Please upload a PDF, DOCX, DOC or TXT file');
      return;
    }
    
    setUploadingResume(true);
    try {
      const token = localStorage.getItem('candidate_token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/candidate/upload-resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        toast.success('Resume uploaded! Parsing in progress...');
        setTimeout(() => {
          loadProfile(token);
          // Navigate to dashboard and show LinkedIn share prompt
          navigate('/my/dashboard');
          setShowResumeSharePrompt(true);
        }, 3000);
      } else {
        let err;
        try {
          err = await res.json();
        } catch {
          err = { detail: 'Server error. Please try again.' };
        }
        toast.error(err.detail || 'Upload failed');
      }
    } catch {
      toast.error('Failed to upload resume');
    } finally {
      setUploadingResume(false);
      if (resumeInputRef.current) resumeInputRef.current.value = '';
    }
  };

  const navItems = [
    { key: 'dashboard', label: 'Dashboard', icon: Home },
    { key: 'profile', label: 'Edit Profile', icon: Edit },
    { key: 'salary-history', label: 'Salary History', icon: TrendingUp },
    { key: 'jobs', label: 'Job Matches', icon: Target },
    { key: 'search', label: 'Find Jobs', icon: Search },
    { key: 'verification', label: 'Verification', icon: Shield },
    { key: 'settings', label: 'Settings', icon: Settings }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="px-4 lg:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-1.5 hover:bg-slate-100 rounded-lg">
              <FileText className="w-5 h-5 text-slate-600" />
            </button>
            <img 
              src={pageSettings?.company_logo_url || "/images/daruwallas-logo.png"} 
              alt={pageSettings?.company_name || "Daruwallas"} 
              className="h-12 lg:h-14 object-contain"
              data-testid="portal-logo"
              onError={(e) => { e.target.src = "/images/daruwallas-logo.png"; }}
            />
          </div>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/${candidate?.username || candidate?.phone}`)}>
              <Eye className="w-4 h-4 mr-2" />View Profile
            </Button>
            <Avatar className="w-9 h-9 cursor-pointer border-2 border-slate-200" onClick={() => navigate('/my/settings')}>
              <AvatarImage src={candidate?.profile_picture?.startsWith('data:') || candidate?.profile_picture?.startsWith('http') ? candidate.profile_picture : (candidate?.profile_picture ? `${API_URL}${candidate.profile_picture}` : null)} />
              <AvatarFallback className="bg-slate-200 text-slate-600 text-sm">{getInitials(formatName(candidate?.name))}</AvatarFallback>
            </Avatar>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Left Sidebar */}
        <aside className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:relative z-40 lg:z-auto w-56 bg-white border-r border-slate-200 h-[calc(100vh-65px)] transition-transform duration-200`}>
          <nav className="p-3 space-y-1">
            {navItems.map(item => {
              const isActive = activeTab === item.key || (item.key === 'dashboard' && (activeTab === 'my' || activeTab === 'dashboard'));
              return (
                <button
                  key={item.key}
                  onClick={() => { navigate(`/my/${item.key === 'dashboard' ? '' : item.key}`); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`portal-nav-${item.key}`}
                >
                  <item.icon className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Resume Section in Sidebar */}
          <div className="p-3 border-t border-slate-100 mt-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-3 mb-2">Resume</p>
            {candidate?.original_file_name ? (
              <div className="space-y-1.5">
                <button
                  onClick={handleViewResume}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                  data-testid="view-resume-btn"
                >
                  <Eye className="w-4 h-4" />
                  View Resume
                </button>
                <button
                  onClick={() => resumeInputRef.current?.click()}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                  data-testid="update-resume-btn"
                >
                  <Upload className="w-4 h-4" />
                  {uploadingResume ? 'Uploading...' : 'Update Resume'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => resumeInputRef.current?.click()}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                data-testid="upload-resume-btn"
              >
                <Upload className="w-4 h-4" />
                {uploadingResume ? 'Uploading...' : 'Upload Resume'}
              </button>
            )}
            <input
              ref={resumeInputRef}
              type="file"
              accept=".pdf,.docx,.doc,.txt"
              onChange={handleUploadResume}
              className="hidden"
            />
            {candidate?.original_file_name && (
              <p className="text-xs text-slate-400 mt-1 px-3 truncate" title={candidate.original_file_name}>
                {candidate.original_file_name}
              </p>
            )}
          </div>
        </aside>

        {/* Overlay for mobile sidebar */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-30 bg-black/20" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <div className="max-w-5xl mx-auto">
            {activeTab === 'dashboard' || activeTab === 'my' ? (
              <Dashboard candidate={candidate} matches={matches} loadMatches={() => loadMatches()} />
            ) : activeTab === 'profile' ? (
              <ProfileEditor 
                candidate={candidate} 
                setCandidate={setCandidate} 
                loadProfile={() => loadProfile(localStorage.getItem('candidate_token'))}
                onAutoUnlock={triggerAutoUnlock}
              />
            ) : activeTab === 'salary-history' ? (
              <SalaryHistoryEditor 
                candidate={candidate} 
                setCandidate={setCandidate}
                loadProfile={() => loadProfile(localStorage.getItem('candidate_token'))}
                onAutoUnlock={triggerAutoUnlock}
              />
            ) : activeTab === 'jobs' ? (
              <JobMatchesTab matches={matches} candidate={candidate} />
            ) : activeTab === 'search' ? (
              <JobSearchPage candidate={candidate} />
            ) : activeTab === 'verification' ? (
              <VerificationTab />
            ) : activeTab === 'settings' ? (
              <SettingsPage 
                candidate={candidate} 
                loadProfile={() => loadProfile(localStorage.getItem('candidate_token'))} 
              />
            ) : null}
          </div>
        </main>
      </div>

      {/* Resume Modal */}
      {showResumeModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowResumeModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                {candidate?.original_file_name || 'Resume'}
              </h3>
              <div className="flex items-center gap-2">
                <a
                  href={`${API_URL}/api/candidate/resume`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <ExternalLink className="w-4 h-4" />Open
                </a>
                <button onClick={() => setShowResumeModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              {candidate?.original_file_name?.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={`${API_URL}/api/candidate/resume`}
                  className="w-full h-full"
                  title="Resume Preview"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500">
                  <div className="text-center">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="font-medium">Preview not available for this file format</p>
                    <a
                      href={`${API_URL}/api/candidate/resume`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />Download to View
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Post-Resume Upload LinkedIn Share Prompt */}
      {showResumeSharePrompt && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="resume-share-prompt">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="bg-gradient-to-br from-[#0077B5] to-[#005885] p-6 text-center text-white">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold">Resume Uploaded Successfully!</h3>
              <p className="text-blue-100 mt-2 text-sm">Share on LinkedIn to unlock AI-powered job matches</p>
            </div>
            <div className="p-6">
              <div className="bg-slate-50 rounded-xl p-4 mb-4 border border-slate-200">
                <p className="text-sm text-slate-700 leading-relaxed">
                  I just uploaded my CV on Daruwallas - India's AI-powered job matching platform! Upload your resume and unlock your job opportunities.
                </p>
                <p className="text-sm text-slate-700 mt-2">The AI matched me with relevant jobs in my locality within seconds.</p>
                <p className="text-sm text-slate-700 mt-2">Check it out: https://daruwallas.com</p>
                <p className="text-sm text-blue-600 mt-2 font-medium">#dajobs #careers #hiring #jobsearch #daruwallas #aijobs #resumeupload</p>
              </div>
              <div className="flex flex-col gap-3">
                <Button
                  className="w-full bg-[#0077B5] hover:bg-[#006097] text-white h-11"
                  onClick={() => {
                    const postText = "I just uploaded my CV on Daruwallas - India's AI-powered job matching platform! Upload your resume and unlock your job opportunities.\n\nThe AI matched me with relevant jobs in my locality within seconds.\n\nCheck it out: https://daruwallas.com\n\n#dajobs #careers #hiring #jobsearch #daruwallas #aijobs #resumeupload";
                    window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(postText)}`, '_blank');
                    const tkn = localStorage.getItem('candidate_token');
                    fetch(`${API_URL}/api/candidate/unlock`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tkn}` },
                      body: JSON.stringify({ action: 'linkedin_share', metadata: { source: 'resume_upload' } })
                    }).then(() => {
                      toast.success('Unlocked! AI is searching for jobs...');
                      setShowResumeSharePrompt(false);
                    }).catch(() => {});
                  }}
                  data-testid="resume-share-linkedin-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />Share on LinkedIn & Unlock Jobs
                </Button>
                <button
                  className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
                  onClick={() => setShowResumeSharePrompt(false)}
                  data-testid="resume-share-skip-btn"
                >Skip for now</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
